import { InvoiceValidation } from '@accounting/interfaces/invoice';
import SearchAndOrder from '@core/interfaces/bpc-search-and-order';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import InvoiceValidationForm from '../../forms/invoice-validation-form/invoice-validation-form.vue';

@Component({
  name: 'bpc-invoice-validation-modal',
  components: {
    'bpc-invoice-validation-form': InvoiceValidationForm,
  },
})
export default class InvoiceGenerationModal extends Vue {
  declare $refs: {
    invoiceValidationForm: InstanceType<typeof InvoiceValidationForm> & {
      formModel: AnyEntity;
    };
  };
  // PROPS
  @Prop({ type: String, default: 'invoice-validation-modal' }) readonly modalId: string;
  @Prop({ type: Boolean, default: false }) readonly loading: boolean;
  @Prop({ type: Boolean, default: false }) readonly showModal: boolean;
  @Prop(Object) entity;
  @Prop({ type: Boolean, default: false }) readonly isBulk: boolean;
  @Prop(Number) readonly selectedAccountingJournalId: number;
  @Prop({
    type: Array,
    default() {
      return [];
    },
  })
  readonly selectedItemIds: number[];
  @Prop({
    type: Object,
    default() {
      return {};
    },
  })
  readonly filterModel;
  @Prop({
    type: Object,
    default() {
      return {};
    },
  })
  readonly searchAndOrder: SearchAndOrder;
  @Prop({
    type: Array,
    default() {
      return ['invoice', 'credit_note'];
    },
  })
  readonly accountingJournalsType: string[];
  @Prop({ type: Boolean, default: false }) readonly showProcessAllOptions: boolean;
  @Prop({ type: Number, default: 0 }) readonly totalEntities: number;
  @Prop({ type: Number, default: 0 }) readonly totalSelectedEntities: number;

  // DATA

  public validateInvoiceErrors = {};
  public isValidatingInvoices: boolean = false;
  public invoiceValidationRequestSent: boolean = false;
  public invoiceValidationEventsError = [];
  public invoiceValidationEventsSuccess = [];
  isLoadingNextInvoiceNumber = false;

  public uniqIdForInvoiceForm = null;

  showModalIndex = null;
  invoiceRoute = '';

  readonly errorMessageText: string = 'global.generic.error_form_action';

  // COMPUTED
  get modalTitle() {
    return this.$t('global.modals.invoice_validation.title');
  }

  get modalBody() {
    return this.$t('global.modals.invoice_validation.body');
  }

  get tenantId(): number {
    return this.$store.getters['tenant/selectedTenantId'];
  }

  // LIFECYCLE
  mounted() {
    this.showModalIndex = Date.now();
    this.initInvoiceValidationVars();
    if (this.showModal) {
      this.openModal();
    }
  }

  initInvoiceValidationVars() {
    this.invoiceValidationEventsError = [];
    this.invoiceValidationEventsSuccess = [];
    this.invoiceValidationRequestSent = false;
    this.isValidatingInvoices = false;
    this.validateInvoiceErrors = null;
  }

  // METHODS
  closeModal() {
    this.showModalIndex = Date.now();
    this.$emit('close-modal');
    this.initInvoiceValidationVars();
  }

  openModal() {
    this.$bvModal.show(this.modalId);
  }

  onConfirmValidateInvoices() {
    const basePath = this.$bpcApiRouteHelper.getEntityPath('invoice');
    this.invoiceValidationRequestSent = true;

    const formModel: InvoiceValidation = this.$refs?.invoiceValidationForm?.formModel;
    const processAll = !!formModel.process_all;
    const payload: AnyEntity = {
      ids: formModel.ids,
      accounting_journal_id: formModel.accounting_journal.id,
      invoice_date: formModel.invoice_date,
      start_from_sequence: formModel.document_sequence,
      due_date: formModel.due_date,
      default_due_date: formModel.default_due_date,
      bulk: this.isBulk,
    };

    if (this.isBulk) {
      const sessionCode = this.getSessionCode();
      this.listenToWebsocketInvoiceValidationChannel(sessionCode);
      this.invoiceValidationRequestSent = true;
      this.isValidatingInvoices = true;
      payload.ids = this.selectedItemIds;
      payload.filters = this.filterModel;
      payload.search_and_order = this.searchAndOrder;
      payload.process_all = processAll;
      payload.session_code = sessionCode;
    }

    this.$bpcApi.post(basePath + '/validate', payload).subscribe(
      res => {
        this.invoiceValidationRequestSent = true;
        if (!this.isBulk) {
          this.closeModal();
          this.invoiceValidationRequestSent = false;
          this.$bpcMessage.showSuccessMessage({ text: 'global.generic.successful_action' });
          this.$emit('invoice-validation-finished');
        }
      },
      error => {
        this.validateInvoiceErrors = error?.response.data.errors;
        this.invoiceValidationRequestSent = false;
        this.isValidatingInvoices = false;
        this.$bpcMessage.showErrorMessage({ text: this.errorMessageText });
      },
    );
  }

  getSessionCode() {
    return `invoice-validation-${this.tenantId}-${this.generateUUIDv4()}`;
  }

  listenToWebsocketInvoiceValidationChannel(sessionCode) {
    this.isValidatingInvoices = true;
    this.$bpcEcho
      .getInvoiceValidationChannel(sessionCode)
      .listen('.BPC\\Accounting\\Events\\InvoicesValidated', data => {
        // If not invoiced genetared dont show succes alert.
        if (data.result.invoices_processed.length) {
          this.$bpcMessage.showInfoMessage({
            text: 'global.accounting.invoices.invoices_validated',
          });
        }
        this.isValidatingInvoices = false;
        this.$emit('invoice-validation-finished');
        this.$bpcEcho.leaveInvoiceValidationChannel(sessionCode);
      })
      .listen('.BPC\\Accounting\\Events\\InvoiceValidated', data => {
        this.invoiceValidationEventsSuccess.push(data);
      })
      .listen('.BPC\\Accounting\\Events\\ErrorValidatingInvoice', data => {
        this.invoiceValidationEventsError.push(data);
      })
      .listen('.BPC\\Accounting\\Events\\GlobalErrorValidatingInvoices', data => {
        this.$bpcMessage.showErrorMessage({
          text: data.msg,
        });
        this.isValidatingInvoices = false;
        this.invoiceValidationRequestSent = false;
        this.$bpcEcho.leaveInvoiceValidationChannel(sessionCode);
      });
  }

  onLoadingNextInvoiceNumber(value) {
    this.isLoadingNextInvoiceNumber = value;
  }

  onValidationWarning(event) {
    console.log('onValidationWarning', event);
  }

  generateUUIDv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = (Math.random() * 16) | 0,
        v = c == 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  // WATCHERS

  @Watch('showModal')
  onShowModalChange(val) {
    if (val) {
      this.openModal();
    } else {
      this.$bvModal.hide(this.modalId);
    }
  }
}
